/**
 * Derived signals. A collapsible card of rows, each a name and a formula in the
 * same editor as the Number chart's calculator, in the derived dialect: bare
 * signals give a value per row (`"Fuel rate" / "STW"`), summaries give a
 * trailing window (`AVG("STW", 1h)`) or one value over the range (`AVG("STW")`).
 * Rows live in the `derivedSignals` atom.
 *
 * Derived columns become plottable pseudo-columns usable in the y-axis, color,
 * and filters; they're never sent on export. A rename cascades to every
 * reference. Typing updates the row as it goes; the chart replots when the
 * formula loses focus and is valid, never mid-word.
 */

import { html, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { derivedSignals, allColumns, selectedShip, type DerivedSignal } from '../../state/store.ts';
import {
  newDerivedSignal,
  derivedFormula,
  isDerivedValid,
  isDerivedNameValid,
  renameDerivedReferences,
  removeDerivedReferences,
  scheduleDerivedReplot,
} from './derived-state.ts';
import { syncToUrl } from '../../state/url-sync.ts';
import { isNumericType } from '../../utils/columns.ts';
import type { ColumnInfo } from '../../api/types.ts';
import '../../ui/number-formula-editor.ts';

@customElement('derived-signals')
export class DerivedSignalsBuilder extends LightElement {
  @state() private collapsed = true;
  /** Rows whose formula changed since it last replotted. */
  private edited = new Set<number>();

  private store = new StoreController(this, [derivedSignals, allColumns, selectedShip]);

  /* ── Helpers ──────────────────────────────────────────── */

  private rawNames(): Set<string> {
    return new Set(allColumns.value.map((c) => c.name));
  }

  /** Numeric raw columns only - derived-on-derived isn't allowed. */
  private numericCols(): ColumnInfo[] {
    return allColumns.value.filter((c) => isNumericType(c.type));
  }

  private rowValid(d: DerivedSignal): boolean {
    const numeric = new Set(this.numericCols().map((c) => c.name));
    return isDerivedValid(d, this.rawNames(), numeric);
  }

  /* ── Row mutations ────────────────────────────────────── */

  private addDerived(): void {
    const list = derivedSignals.value;
    derivedSignals.set([...list, newDerivedSignal(list)]);
    this.collapsed = false;
    // A new row has no formula yet - nothing to replot.
    syncToUrl();
    void this.updateComplete.then(() => {
      this.querySelector<HTMLTextAreaElement>('.derived-row:last-child textarea')?.focus();
    });
  }

  private removeDerived(idx: number): void {
    const list = derivedSignals.value;
    const d = list[idx];
    if (!d) return;
    derivedSignals.set(list.filter((_, i) => i !== idx));
    this.edited.clear();
    removeDerivedReferences(d.name);
    syncToUrl();
    scheduleDerivedReplot();
  }

  private rename(idx: number, value: string): void {
    const list = derivedSignals.value;
    const d = list[idx];
    const name = value.trim();
    if (!d || name === d.name || !isDerivedNameValid(name, idx, list, this.rawNames())) return;
    const next = { ...d, name };
    derivedSignals.set(list.map((o, i) => (i === idx ? next : o)));
    renameDerivedReferences(d.name, name);
    syncToUrl();
    if (this.rowValid(next)) scheduleDerivedReplot();
  }

  private setFormula(idx: number, expr: string): void {
    const list = derivedSignals.value;
    const d = list[idx];
    if (!d || derivedFormula(d) === expr) return;
    derivedSignals.set(list.map((o, i) => (i === idx ? { ...d, expr } : o)));
    this.edited.add(idx);
    syncToUrl();
  }

  /** Replot once the formula is left, if it changed and now computes. */
  private settle(idx: number, e: FocusEvent): void {
    const row = e.currentTarget as HTMLElement;
    if (row.contains(e.relatedTarget as Node | null) || !this.edited.delete(idx)) return;
    const d = derivedSignals.value[idx];
    if (d && this.rowValid(d)) scheduleDerivedReplot();
  }

  /* ── Rendering ────────────────────────────────────────── */

  private renderRow(d: DerivedSignal, idx: number): TemplateResult {
    const list = derivedSignals.value;
    const nameValid = isDerivedNameValid(d.name ?? '', idx, list, this.rawNames());
    const nameId = `derived-name-${idx}`;
    return html`
      <div class="derived-row" @focusout=${(e: FocusEvent) => this.settle(idx, e)}>
        <div class="derived-head">
          <span class="derived-glyph" aria-hidden="true">ƒ</span>
          <input
            id=${nameId}
            type="text"
            aria-label="Signal name"
            class="derived-name ${nameValid ? '' : 'invalid'}"
            placeholder="Signal name"
            title="The name you pick in axes, color and filters"
            .value=${d.name}
            @input=${(e: Event) => {
              const el = e.target as HTMLInputElement;
              el.classList.toggle('invalid', !isDerivedNameValid(el.value.trim(), idx, list, this.rawNames()));
            }}
            @change=${(e: Event) => this.rename(idx, (e.target as HTMLInputElement).value)}
          />
          <button class="remove-btn" title="Remove" aria-label=${`Remove ${d.name}`}
            @click=${() => this.removeDerived(idx)}>✕</button>
        </div>
        <number-formula-editor
          dialect="derived"
          label="Formula"
          .columns=${this.numericCols()}
          .value=${derivedFormula(d)}
          .change=${(v: string) => this.setFormula(idx, v)}
        ></number-formula-editor>
      </div>
    `;
  }

  override render(): TemplateResult {
    const list = derivedSignals.value;
    const count = list.length;
    const addDisabled = !selectedShip.value || allColumns.value.length === 0;
    return html`
      <div class="card" data-tab="plot">
        <div class="section-header" @click=${() => (this.collapsed = !this.collapsed)}>
          <span class="arrow ${this.collapsed ? '' : 'open'}">▶</span>
          <span>Derived signals</span>
          <span class="count">${count > 0 ? `(${count})` : ''}</span>
        </div>
        <div class="section-body ${this.collapsed ? 'collapsed' : ''}">
          <div id="derived-container">
            ${count === 0
              ? html`<div class="series-empty">No derived signals</div>`
              : list.map((d, i) => this.renderRow(d, i))}
          </div>
          <div class="derived-actions">
            <button
              class="btn btn-secondary"
              ?disabled=${addDisabled}
              @click=${() => this.addDerived()}
            >
              + Add derived
            </button>
            <span class="derived-hint">Usable in axes, histogram, color, and filters.</span>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'derived-signals': DerivedSignalsBuilder;
  }
}
